import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import * as Sentry from '@sentry/react';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { Alert, Box, Button, CircularProgress, LinearProgress, Stack, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

import { makeAuthenticatedApiCall } from '../../../apiHelper';
import legacyTheme from '../../../theme';
import { tokens } from '../../../theme-next';
import type { TaxData } from '../../../types/cg';
import { useAuth } from '../../AuthContext';
import { useBackgroundUploads } from '../../BackgroundUploads/BackgroundUploadsProvider';
import { CgAuditBatchApiResponse, getIaTraceText, traceIa } from '../iaTrace';
import { mapStoreToCreateCgPayload } from '../ocr/mapStoreToCreateCgPayload';
import { mergeExtractedFieldsToFormValues } from '../ocr/mergeExtractedFieldsToFormValues';
import { useFormCGIAStore } from '../store/useFormCGIAStore';
import { isCtRequiredFromSession } from '../validation/ctRequirement';
import { PM_NOT_REQUIRED_DOC_TYPES, isAcheteurPersonneMorale } from '../validation/personneMorale';

// ─────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────

const toPct = (confidenceFraction: number): number => Math.round(confidenceFraction * 100);

interface CreateCgResponse {
  success: boolean;
  registrationId?: number;
  error?: string;
  message?: string;
}

interface GenerateDealIdResponse {
  id: number;
  add_time: string;
  stage_id: number;
}

interface AgencyInfo {
  agencyID: number | string | undefined;
  personID: number | string | undefined;
  agencyEmail: string | undefined;
  agencyName: string | undefined;
  agencyAddress: string | undefined;
}

type SubmitPhase = 'idle' | 'creating' | 'computing_taxes' | 'finalizing' | 'done' | 'error';

// ─────────────────────────────────────────────────────────────
//  RecapAgencyScreen — finalisation pure
//
//  Refonte : plus de re-vérification manuelle (plaque/dép/coût) ni de
//  checkbox d'acquit. Les cross-checks IA (incl. rule #19 user-input vs
//  pieces) ont déjà signalé toute divergence à l'étape précédente. Cet
//  écran est uniquement informatif : on lance immédiatement Phase 1 +
//  Phase 2 atomique au mount et on affiche la progression dans une UI
//  moderne et rassurante.
// ─────────────────────────────────────────────────────────────

export const RecapAgencyScreen = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const extractedFields = useFormCGIAStore((s) => s.extractedFields);
  const uploadedDocs = useFormCGIAStore((s) => s.uploadedDocs);
  // Le routage (personne × face) a arbitré les identités dès qu'au moins une
  // pièce d'identité est passée par le pool — le filet de départage à la
  // soumission ne doit alors pas ré-écraser les valeurs du formulaire.
  const identityRouted = useFormCGIAStore((s) => s.identityDocsPool.length > 0);
  const ingestionItems = useFormCGIAStore((s) => s.ingestionItems);
  const hebergementUploads = useFormCGIAStore((s) => s.hebergementUploads);
  const kbisUrls = useFormCGIAStore((s) => s.kbisUrls);
  const coBuyerFiles = useFormCGIAStore((s) => s.coBuyerFiles);
  const agencyComment = useFormCGIAStore((s) => s.agencyComment);
  const agencyCommentFiles = useFormCGIAStore((s) => s.agencyCommentFiles);
  // Snapshot d'analyse figé par FormCGIAv2 — envoyé tel quel au backend pour
  // être rejoué à l'identique par la page review (iso garantie).
  const analysisSnapshot = useFormCGIAStore((s) => s.analysisSnapshot);
  const setV2Step = useFormCGIAStore((s) => s.setV2Step);
  const resetV2 = useFormCGIAStore((s) => s.resetV2);
  const storedRegPlate = useFormCGIAStore((s) => s.regPlate);
  const storedDepartment = useFormCGIAStore((s) => s.department);
  const storedTaxData = useFormCGIAStore((s) => s.taxData);
  const activeInFlightId = useFormCGIAStore((s) => s.activeInFlightId);
  const { patchUpload } = useBackgroundUploads();

  const missingDocTypes = useMemo(() => {
    const REQUIRED = [
      'carte_grise',
      'cerfa_cession',
      'cerfa_demande_immat',
      'mandat',
      'assurance',
      'controle_technique',
      'piece_identite',
      'permis_conduire',
      'justif_domicile',
    ] as const;
    // Véhicule ≤ 4 ans (MEC MiSIV/OCR) ou X.1 encore valide → le CT n'est
    // pas exigible : on ne le compte pas manquant, sinon le backend crée
    // une document_request impossible à satisfaire (le PV n'existe pas).
    const ctRequired = isCtRequiredFromSession({
      extractedFields,
      taxData: storedTaxData,
      now: new Date(),
    });
    // Acquéreur personne morale : ni permis (une société n'en a pas) ni
    // justificatif de domicile (le Kbis fait foi via l'adresse de
    // l'établissement). MÊME détection que la checklist FormCGIAv2 et que la
    // grille de review — sans ça, ce tableau créait deux
    // `cg_document_requests` impossibles à satisfaire et laissait le dossier
    // collé en « Pièces à déposer » (EH-214-VH, retour François 2026-08-06).
    const acheteurEstPersonneMorale = isAcheteurPersonneMorale({
      flagCession: extractedFields.acheteur_est_personne_morale?.field.value,
      // Kbis de l'ACQUÉREUR uniquement (cf. isAcheteurPersonneMorale) : un Kbis
      // vendeur ne retire pas le permis / JDC de l'acheteur.
      hasKbis: Boolean(uploadedDocs.kbis_acheteur) || Boolean(uploadedDocs.kbis) || kbisUrls.length > 0,
      acheteurSiret: extractedFields.acheteur_siret?.field.value,
      acheteurRaisonSociale: extractedFields.acheteur_raison_sociale?.field.value,
    });
    return REQUIRED.filter((dt) => {
      if (dt === 'controle_technique' && !ctRequired) return false;
      if (acheteurEstPersonneMorale && PM_NOT_REQUIRED_DOC_TYPES.includes(dt)) return false;
      const doc = uploadedDocs[dt];
      return !doc || (doc.firebaseUrls?.length ?? 0) === 0;
    });
  }, [uploadedDocs, extractedFields, storedTaxData, kbisUrls]);

  const [phase, setPhase] = useState<SubmitPhase>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [registrationId, setRegistrationId] = useState<number | null>(null);
  const [taxData, setTaxData] = useState<TaxData | null>(storedTaxData as TaxData | null);
  const [finalizingProgress, setFinalizingProgress] = useState(0);
  const [finalizingLabel, setFinalizingLabel] = useState('');

  const { values: extractedValues } = useMemo(
    () => mergeExtractedFieldsToFormValues(extractedFields),
    [extractedFields],
  );

  const agencyInfo: AgencyInfo = useMemo(
    () => ({
      agencyID: (currentUser as any)?.data?.organization?.value ?? (currentUser as any)?.data?.data?.idPipedrive,
      personID: (currentUser as any)?.data?.id,
      agencyEmail:
        (currentUser as any)?.data?.organization_email ||
        (currentUser as any)?.data?.primary_email ||
        (currentUser as any)?.data?.email,
      agencyName: (currentUser as any)?.data?.organization?.name,
      agencyAddress: (currentUser as any)?.data?.organization?.address,
    }),
    [currentUser],
  );

  // Score global pour le flag ai_confidence envoyé au backend.
  const aiConfidence = useMemo(() => {
    const confidences = Object.values(extractedFields).map((e) => e.field.confidence);
    if (confidences.length === 0) return 0;
    const avg = confidences.reduce((sum, c) => sum + c, 0) / confidences.length;
    return toPct(avg);
  }, [extractedFields]);

  const effectiveRegPlate = storedRegPlate || (extractedValues.immatriculation as string) || '';

  // ── Phase 1 : création CG + attach + ECG ─────────────────────
  const runPhase1 = async (): Promise<{ registrationId: number; taxData: TaxData } | null> => {
    setErrorMessage(null);
    setPhase('creating');
    try {
      const mergedValues = {
        ...extractedValues,
        immatriculation: effectiveRegPlate || extractedValues.immatriculation,
      } as any;

      // Toutes les pièces déposées pendant la session, y compris celles qu'aucun
      // slot ne réclame : le mapper y prélève le reliquat `otherFilesUrls` pour
      // qu'aucun fichier envoyé par l'agence ne se perde avant CG_Documents.
      const allUploadedUrls = Object.values(ingestionItems)
        .map((it) => it.firebaseUrl)
        .filter((u): u is string => typeof u === 'string' && u.length > 0);

      const payload = {
        ...mapStoreToCreateCgPayload({
          formValues: mergedValues,
          uploadedDocs,
          agencyInfo,
          hebergementUploads,
          kbisUrls,
          coBuyerFiles,
          agencyComment,
          agencyCommentFiles,
          identityRouted,
          allUploadedUrls,
        }),
        source: 'cgia_v2',
        aiConfidence,
        missingDocTypes,
        analysisSnapshot,
      };

      const createResponse = await makeAuthenticatedApiCall<CreateCgResponse>('post', '/api/cg', payload);
      const newRegistrationId = createResponse.data?.registrationId;
      if (!createResponse.data?.success || !newRegistrationId) {
        throw new Error(
          createResponse.data?.error || createResponse.data?.message || 'Échec de la création de la demande.',
        );
      }
      setRegistrationId(newRegistrationId);

      if (allUploadedUrls.length > 0) {
        try {
          await makeAuthenticatedApiCall('patch', '/api/cg-extractions/attach', {
            imageUrls: allUploadedUrls,
            registrationId: newRegistrationId,
          });
        } catch (attachError) {
          console.warn('cg-extractions/attach failed (non-blocking):', attachError);
        }
      }

      // Log [FormCGIA·IA] verbatim de l'ingestion — persisté APRÈS création via un
      // endpoint dédié (body limit large), PAS dans le payload /api/cg qui doit
      // rester sous la limite globale (sinon 413 sur les gros dossiers). Plafonné
      // côté client et best-effort : ne doit jamais bloquer la soumission.
      try {
        const MAX_TRACE_CHARS = 2_000_000;
        const fullTrace = getIaTraceText();
        const ingestionTrace =
          fullTrace.length > MAX_TRACE_CHARS
            ? `…[trace tronquée — ${fullTrace.length - MAX_TRACE_CHARS} caractères de tête omis]…\n${fullTrace.slice(-MAX_TRACE_CHARS)}`
            : fullTrace;
        if (ingestionTrace.length > 0) {
          await makeAuthenticatedApiCall('patch', `/api/cg/${newRegistrationId}/ingestion-trace`, {
            ingestionTrace,
          });
        }
      } catch (traceError) {
        console.warn('ingestion-trace persist failed (non-blocking):', traceError);
      }

      // Rattache le verdict d'audit déjà calculé à la registration — mode
      // ADOPT-ONLY : on ne PAIE jamais un nouvel appel Claude ici. La seule
      // analyse payée est le préflight (déclenché à l'étape recap, set
      // d'images figé). Cet appel se contente d'adopter ce verdict par
      // images_hash. S'il est encore en vol (course recap → soumission), le
      // backend répond `pending` sans rien dépenser, et la review GOD
      // l'adoptera plus tard (cf. getCGReviewController). Évite le double
      // appel Opus observé auparavant (préflight + soumission en parallèle).
      traceIa(
        'AUDIT-OPUS',
        `Dossier soumis — registration #${newRegistrationId} créée. Rattachement du verdict d'audit (adopt-only): ` +
          `POST /api/cg/${newRegistrationId}/audit-batch?adoptOnly=1. AUCUN nouvel appel Claude — on adopte le verdict du ` +
          `préflight déjà payé. Si le préflight est encore en cours, le verdict sera rattaché à l'ouverture de la review GOD.`,
      );
      void (async () => {
        try {
          const auditResponse = await makeAuthenticatedApiCall<CgAuditBatchApiResponse>(
            'post',
            `/api/cg/${newRegistrationId}/audit-batch?adoptOnly=1`,
          );
          if (auditResponse.data?.pending) {
            traceIa(
              'AUDIT-OPUS',
              `Verdict d'audit pas encore disponible (préflight en cours) — aucun appel Claude payé. ` +
                `Il sera rattaché automatiquement à l'ouverture de la review GOD.`,
            );
            return;
          }
          const audit = auditResponse.data?.data;
          if (!auditResponse.data?.success || !audit) {
            traceIa('AUDIT-OPUS', `Audit batch sans verdict exploitable: ${JSON.stringify(auditResponse.data)}`);
            return;
          }
          traceIa(
            'AUDIT-OPUS',
            `Audit batch terminé — verdict: ${audit.verdict} (modèle ${audit.modelUsed}` +
              `${audit.cached ? ', servi depuis le cache' : ''}, tokens: ${audit.promptTokens ?? '?'} entrée / ` +
              `${audit.completionTokens ?? '?'} sortie).`,
          );
          if (audit.markdown) {
            traceIa(
              'AUDIT-OPUS',
              `Rapport d'audit complet rédigé par Claude Opus (à copier tel quel) :\n${audit.markdown}`,
            );
          }
        } catch (auditError: any) {
          // Non-bloquant : si l'audit batch échoue (quota Anthropic, OCR
          // manquant, etc.), god pourra le relancer manuellement depuis
          // la page review via le bouton "Lancer l'audit IA".
          console.warn('audit-batch trigger failed (non-blocking):', auditError);
          traceIa(
            'AUDIT-OPUS',
            `Échec de l'audit batch (non bloquant — relançable depuis la review GOD): ${auditError?.response?.data?.error ?? auditError?.message ?? 'erreur inconnue'}`,
          );
        }
      })();

      let resolvedTaxData: TaxData | null = null;
      if (storedTaxData) {
        resolvedTaxData = storedTaxData as TaxData;
        setTaxData(resolvedTaxData);
      } else {
        setPhase('computing_taxes');
        const ecgResponse = await makeAuthenticatedApiCall<TaxData>('post', '/api/misiv/ecg', {
          regPlate: effectiveRegPlate,
          department: storedDepartment,
        });
        if (!ecgResponse.data || typeof ecgResponse.data.taxeTotale !== 'number') {
          throw new Error('Le calcul des taxes a échoué (réponse invalide du serveur).');
        }
        resolvedTaxData = ecgResponse.data;
        setTaxData(resolvedTaxData);
      }
      return { registrationId: newRegistrationId, taxData: resolvedTaxData };
    } catch (err: any) {
      // `message` AVANT `error` : `message` porte l'explication rédigée pour
      // l'agence, `error` le détail technique (`err.message` côté serveur).
      // Dans l'autre ordre, l'agence lisait le SQL brut de mysql2 — « Data too
      // long for column 'Extension' at row 1 » affiché tel quel sur l'écran de
      // soumission (FW-262-LA, 12/08/2026).
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Une erreur est survenue pendant la création de la demande.';
      setErrorMessage(message);
      setPhase('error');
      return null;
    }
  };

  // ── Phase 2 : deal-id + Qonto ────────────────────────────────
  const runPhase2 = async (explicitRegistrationId?: number, explicitTaxData?: TaxData) => {
    const effectiveRegistrationId = explicitRegistrationId ?? registrationId;
    const effectiveTaxData = explicitTaxData ?? taxData;
    if (!effectiveTaxData || !effectiveRegistrationId) return;
    setErrorMessage(null);
    setPhase('finalizing');
    if (activeInFlightId) {
      void patchUpload(activeInFlightId, { state: 'submitting' } as any);
    }
    try {
      setFinalizingLabel("Génération de l'identifiant interne…");
      setFinalizingProgress(20);
      // `registrationId` en paramètre : au réessai, le backend rend le deal
      // déjà attaché au lieu d'en tirer un neuf. Sans lui, chaque clic sur
      // « Réessayer la soumission » faisait émettre une facture Qonto de plus.
      const dealResponse = await makeAuthenticatedApiCall<GenerateDealIdResponse>(
        'get',
        `/api/cg/generate-deal-id?registrationId=${effectiveRegistrationId}`,
      );
      const deal = dealResponse.data;

      setFinalizingLabel("Mise à jour de l'enregistrement avec l'identifiant…");
      setFinalizingProgress(45);
      await makeAuthenticatedApiCall('post', `/api/cg/update-pipedrive-deal-id/${effectiveRegistrationId}`, {
        dealId: deal.id,
        taxData: effectiveTaxData,
      });

      // ── Facturation : best-effort, ne doit JAMAIS faire échouer la soumission ──
      // À ce stade la demande EXISTE (registration + deal rattaché). Un échec de
      // facturation (ex: 504 du prestataire, cf. doublon FL-969-JW du 22/07) ne
      // doit pas s'afficher comme « La soumission a échoué » : l'agence resoumet
      // alors tout le dossier et crée un doublon. On loggue (Sentry) et on
      // termine en succès — la facture sera régularisée par la facturation
      // périodique / l'écran des factures en attente côté GOD.
      try {
        setFinalizingLabel('Génération de la facture Qonto…');
        setFinalizingProgress(75);
        await makeAuthenticatedApiCall('post', '/api/qonto/generate', {
          pipeline: 'f',
          deal: {
            id: deal.id,
            add_time: deal.add_time,
            stage_id: deal.stage_id,
            regPlates: [effectiveRegPlate],
            sellingPrices: [effectiveTaxData.taxeTotale.toString()],
            commissionCount: 1,
          },
          vat: 0,
          sellingPrice: effectiveTaxData.taxeTotale,
          agencyData: agencyInfo,
          regPlate: effectiveRegPlate,
        });

        setFinalizingLabel('Synchronisation des factures…');
        setFinalizingProgress(95);
        await makeAuthenticatedApiCall('post', '/api/qonto/sync/invoices/cg');
      } catch (invoiceErr) {
        Sentry.captureException(invoiceErr, {
          tags: { flow: 'form-cgia-v2', step: 'invoice-generation' },
          extra: { dealId: deal.id, registrationId: effectiveRegistrationId, regPlate: effectiveRegPlate },
        });
        console.error('FormCGIA v2 : échec facturation post-soumission (non bloquant)', invoiceErr);
      }

      setFinalizingProgress(100);
      setPhase('done');
      if (activeInFlightId) {
        void patchUpload(activeInFlightId, {
          state: 'done',
          registration_id: effectiveRegistrationId,
          deal_id: deal.id,
        } as any);
      }
      resetV2();
      navigate('/list-cg');
    } catch (err: any) {
      // Même ordre qu'en phase 1 : l'explication métier prime sur le détail technique.
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Une erreur est survenue pendant la finalisation.';
      setErrorMessage(message);
      setPhase('error');
    }
  };

  const runFullPipeline = async () => {
    if (registrationId && taxData) {
      await runPhase2();
      return;
    }
    const result = await runPhase1();
    if (!result) return;
    await runPhase2(result.registrationId, result.taxData);
  };

  // Auto-lance le pipeline une seule fois au mount. Un guard ref évite
  // les double-déclenchements de React Strict Mode en développement.
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void runFullPipeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── UI ───────────────────────────────────────────────────────

  const phaseLabel = (() => {
    switch (phase) {
      case 'idle':
      case 'creating':
        return 'Création de la demande…';
      case 'computing_taxes':
        return 'Calcul des taxes ANTS…';
      case 'finalizing':
        return finalizingLabel || 'Finalisation en cours…';
      case 'done':
        return 'Terminé — redirection…';
      case 'error':
        return 'La soumission a échoué';
      default:
        return '';
    }
  })();

  const progressValue = (() => {
    if (phase === 'creating') return 10;
    if (phase === 'computing_taxes') return 25;
    if (phase === 'finalizing') return Math.max(45, finalizingProgress);
    if (phase === 'done') return 100;
    return 0;
  })();

  const isError = phase === 'error';
  const isDone = phase === 'done';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '32px',
        padding: { xs: '32px 16px', sm: '60px 40px' },
        maxWidth: 720,
        margin: '0 auto',
      }}
    >
      {/* ── Header — focus sur l'action en cours ─────────────── */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            color: tokens.color.yellow,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
          }}
        >
          Soumission de la demande
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: '30px', sm: '38px' },
            fontWeight: 900,
            color: tokens.color.dark,
            letterSpacing: '-0.02em',
            lineHeight: 1.05,
            marginTop: '6px',
          }}
        >
          {isError ? 'Soumission interrompue' : isDone ? 'Demande créée avec succès' : 'On finalise votre demande…'}
        </Typography>
        <Typography
          sx={{
            fontSize: '15px',
            fontWeight: 500,
            color: tokens.color.textSecondary,
            marginTop: '12px',
            lineHeight: 1.55,
          }}
        >
          {isError
            ? "Vous pouvez réessayer — aucun double n'est créé côté Skipcar."
            : isDone
              ? 'Vous allez être redirigé vers la liste de vos demandes.'
              : "Merci de ne pas fermer cette page ni rafraîchir tant que la soumission n'est pas terminée — la création du dossier et la facturation s'effectuent depuis cet onglet."}
        </Typography>
      </Box>

      {/* ── Bloc progression / état ──────────────────────────── */}
      <Box
        sx={{
          width: '100%',
          padding: { xs: '24px', sm: '32px 36px' },
          borderRadius: `${tokens.radius['2xl']}px`,
          background: isError
            ? `linear-gradient(135deg, ${tokens.color.redLight} 0%, #FFFFFF 80%)`
            : isDone
              ? `linear-gradient(135deg, ${tokens.color.greenLight ?? '#E6F9EF'} 0%, #FFFFFF 80%)`
              : `linear-gradient(135deg, ${tokens.color.yellowLight} 0%, #FFFFFF 80%)`,
          border: `1px solid ${
            isError ? tokens.color.redBorder : isDone ? (tokens.color.green ?? '#10B981') : tokens.color.yellow
          }`,
          boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '18px' }}>
          {isError ? (
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                backgroundColor: tokens.color.red,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 22,
                fontWeight: 900,
              }}
            >
              !
            </Box>
          ) : isDone ? (
            <CheckCircleRoundedIcon sx={{ fontSize: 36, color: tokens.color.green ?? '#10B981' }} />
          ) : (
            <CircularProgress size={28} thickness={4} sx={{ color: tokens.color.yellow }} />
          )}
          <Typography
            sx={{
              fontSize: '17px',
              fontWeight: 700,
              color: tokens.color.dark,
              flex: 1,
              lineHeight: 1.3,
            }}
          >
            {phaseLabel}
          </Typography>
        </Box>

        {!isError && !isDone && (
          <>
            <LinearProgress
              variant="determinate"
              value={progressValue}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: '#F0F0EE',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: tokens.color.yellow,
                  borderRadius: 5,
                },
              }}
            />

            {/* Signal "en parallèle Skipcar fait un truc bien" : l'audit batch
                Claude Opus tourne en sous-marin pendant runPhase2. Permet à
                l'agence de comprendre la valeur ajoutée vs un simple submit
                de form, sans la polluer avec les détails techniques. */}
            <Box
              sx={{
                marginTop: '14px',
                padding: '10px 14px',
                borderRadius: 2,
                backgroundColor: '#FFFBEB',
                border: `1px solid ${tokens.color.yellow}`,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <Box
                sx={{
                  fontSize: 18,
                  // Petite animation pulse pour faire vivre le ✨ — modeste,
                  // pas distrayante.
                  animation: 'sk-pulse-soft 1.6s ease-in-out infinite',
                  '@keyframes sk-pulse-soft': {
                    '0%, 100%': { opacity: 0.65 },
                    '50%': { opacity: 1 },
                  },
                }}
              >
                ✨
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: tokens.color.dark, lineHeight: 1.3 }}>
                  Analyse globale IA en cours
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: tokens.color.textSecondary,
                    marginTop: '2px',
                    lineHeight: 1.4,
                  }}
                >
                  Notre agent IA relit l'intégralité de votre dossier (toutes pièces croisées) pour préparer la revue
                  Skipcar. Aucune action requise.
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {errorMessage && isError && (
          <Alert severity="error" sx={{ fontSize: '14px', marginTop: '14px' }}>
            {errorMessage}
          </Alert>
        )}
      </Box>

      {/* ── Actions : seulement en cas d'erreur ──────────────── */}
      {isError && (
        <ThemeProvider theme={legacyTheme}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button className="secondaryDealActionButton" onClick={() => setV2Step('ingesting')}>
              ← Retour aux documents
            </Button>
            <Button
              className="dealActionButton"
              onClick={() => void runFullPipeline()}
              color="primary"
              variant="contained"
              startIcon={<RefreshRoundedIcon />}
            >
              Réessayer la soumission
            </Button>
          </Stack>
        </ThemeProvider>
      )}
    </Box>
  );
};
